import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, X, Vote, Heart, Award, ArrowRight, ShieldCheck, Info } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const ICONS = {
  community: Heart,
  annual: Vote,
  lifetime: Award,
};

const COMPARE_ROWS = [
  ["Community access", true, true, true],
  ["Educational resources", true, true, true],
  ["Volunteer opportunities", true, true, true],
  ["Newsletters & event updates", true, true, true],
  ["Online events & webinars", "Selected", "Priority", "VIP"],
  ["In-person event tickets", "Limited", "Priority", "VIP"],
  ["Voting rights at AGM", false, "After 90 days", "Lifetime"],
  ["Eligibility to contest elections", false, "Limited", "Yes"],
  ["Committee participation", false, true, true],
  ["Founding Wall recognition", false, false, true],
  ["Strategic advisory access", false, false, true],
];

function CompareCell({ value }) {
  if (value === true) return <Check className="text-saffron mx-auto" size={18} />;
  if (value === false) return <X className="text-slate-300 mx-auto" size={18} />;
  return <span className="text-xs text-navy/80">{value}</span>;
}

export default function Membership() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [voteDays, setVoteDays] = useState(90);
  const [submitting, setSubmitting] = useState(null);

  useEffect(() => {
    api.get("/membership/tiers")
      .then(({ data }) => {
        setTiers(data.tiers || []);
        setVoteDays(data.voting_qualify_days || 90);
      })
      .catch(() => {});
  }, []);

  const isPaidMember = useMemo(
    () => user && ["annual", "supporting", "lifetime"].includes(user.membership_tier),
    [user]
  );

  const handleCommunity = async () => {
    if (!user) {
      toast.info("Create your free account first");
      navigate("/register");
      return;
    }
    setSubmitting("community");
    try {
      const { data } = await api.post("/membership/join-community");
      toast.success(data.message || "Welcome to the community");
      await refresh();
      navigate("/profile");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(null);
    }
  };

  const handleCheckout = async (tierId) => {
    if (!user) {
      toast.info("Please create your account first, then come back to upgrade");
      navigate("/register");
      return;
    }
    setSubmitting(tierId);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: tierId,
        package_type: "membership",
        origin_url: window.location.origin,
        donor_name: user.name,
        donor_email: user.email,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSubmitting(null);
    }
  };

  return (
    <>
      {/* HERO */}
      <section className="bg-navy text-pearl py-20 lg:py-28" data-testid="membership-hero">
        <div className="section-container max-w-5xl">
          <p className="eyebrow text-gold">Become a member</p>
          <h1 className="mt-4 font-display text-5xl sm:text-6xl leading-[1.05]">
            Three doors. <span className="italic text-gold">One sangha.</span>
          </h1>
          <p className="mt-7 text-pearl/80 max-w-2xl text-lg leading-relaxed">
            Whether you're exploring Buddhism, looking to volunteer, or ready to help shape the
            society's future, there's a place for you here. Start free and grow into governance
            when you're ready.
          </p>
          <div className="mt-6 inline-flex items-start gap-2 px-4 py-3 bg-pearl/10 border border-saffron/40 rounded-sm text-xs text-pearl/85 max-w-2xl" data-testid="membership-cio-notice">
            <span className="text-saffron font-semibold">Please note:</span>
            <span>
              Charity registration is in progress (CIO application in preparation).
              Paid membership tiers are <strong>optional supporter contributions</strong>
              {" "}and confer no exclusive religious benefit — hardship waivers are
              available on request and remain confidential to the trustees.
            </span>
          </div>
          {user && (
            <div className="mt-8 inline-flex items-center gap-2 px-4 py-2 bg-pearl/10 border border-gold/30 rounded-full text-sm" data-testid="current-tier-pill">
              <ShieldCheck size={14} className="text-gold" />
              You're currently: <strong className="text-gold">{user.membership_tier_label || "Community Member"}</strong>
            </div>
          )}
        </div>
      </section>

      {/* THREE TIERS */}
      <section className="py-20 bg-pearl" data-testid="membership-tiers">
        <div className="section-container max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {tiers.map((t, idx) => {
              const Icon = ICONS[t.id] || Heart;
              const featured = t.id === "annual";
              const isCurrent = user?.membership_tier === t.id;
              return (
                <div
                  key={t.id}
                  data-testid={`tier-card-${t.id}`}
                  className={`flex flex-col bg-white border rounded-lg overflow-hidden transition-all ${
                    featured ? "border-gold ring-2 ring-gold/30 shadow-2xl lg:-translate-y-2" : "border-navy/10"
                  }`}
                >
                  {featured && (
                    <p className="bg-gold text-navy text-[10px] tracking-[0.22em] uppercase font-semibold text-center py-1.5">
                      Most popular
                    </p>
                  )}
                  <div className="p-7 sm:p-8 flex-1 flex flex-col">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-saffron/15 text-saffron">
                        <Icon size={20} strokeWidth={1.6} />
                      </span>
                      <span className="text-[10px] uppercase tracking-[0.22em] text-saffron font-semibold">
                        Tier {idx + 1}
                      </span>
                    </div>
                    <h3 className="mt-4 font-display text-3xl text-navy">{t.label}</h3>
                    <p className="mt-1 text-sm text-slate-600 italic">{t.tagline}</p>
                    <p className="mt-6 font-display text-5xl text-navy">
                      {t.price_label}
                    </p>
                    <p className="mt-4 text-sm text-slate-700 leading-relaxed">{t.summary}</p>

                    {t.benefits?.length > 0 && (
                      <ul className="mt-6 space-y-2.5 text-sm text-slate-700">
                        {t.benefits.map((b) => (
                          <li key={b} className="flex gap-2"><Check size={15} className="text-gold mt-0.5 shrink-0" />{b}</li>
                        ))}
                      </ul>
                    )}

                    {t.restrictions?.length > 0 && (
                      <ul className="mt-5 pt-5 border-t border-navy/10 space-y-2 text-xs text-slate-500">
                        {t.restrictions.map((r) => (
                          <li key={r} className="flex gap-2"><X size={12} className="text-slate-400 mt-0.5 shrink-0" />{r}</li>
                        ))}
                      </ul>
                    )}

                    <div className="mt-auto pt-7">
                      {isCurrent ? (
                        <div className="text-center py-3 px-4 bg-saffron/10 text-saffron text-sm rounded-sm border border-saffron/30 font-medium">
                          ✓ Your current tier
                        </div>
                      ) : t.checkout ? (
                        <button
                          onClick={() => handleCheckout(t.id)}
                          disabled={submitting === t.id || isPaidMember && t.id === "annual"}
                          data-testid={`tier-cta-${t.id}`}
                          className={`w-full py-3.5 rounded-sm font-medium ${featured ? "bg-gold text-navy hover:bg-gold-light" : "bg-navy text-pearl hover:bg-navy-light"} disabled:opacity-60`}
                        >
                          {submitting === t.id ? "Redirecting…" : t.cta}
                        </button>
                      ) : (
                        <button
                          onClick={handleCommunity}
                          disabled={submitting === t.id}
                          data-testid={`tier-cta-${t.id}`}
                          className="w-full py-3.5 border border-navy/20 text-navy rounded-sm hover:bg-navy/5 disabled:opacity-60 font-medium"
                        >
                          {submitting === t.id ? "Joining…" : t.cta}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-10 flex items-start gap-3 text-sm text-slate-600 max-w-3xl mx-auto" data-testid="voting-rule">
            <Info size={16} className="text-gold flex-shrink-0 mt-0.5" />
            <p>
              <strong className="text-navy">Voting rule.</strong> To prevent last-minute signups
              from influencing elections, only paid members with at least <strong>{voteDays} days</strong>
              {" "}of continuous active membership before the AGM date are eligible to vote. Lifetime members are
              always eligible.
            </p>
          </div>
        </div>
      </section>

      {/* COMPARE TABLE */}
      <section className="py-20 bg-white border-t border-navy/10" data-testid="membership-compare">
        <div className="section-container max-w-5xl">
          <p className="eyebrow">Compare at a glance</p>
          <h2 className="mt-3 font-display text-4xl text-navy">What's in each tier.</h2>
          <div className="mt-10 overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="membership-compare-table">
              <thead>
                <tr className="border-b-2 border-navy">
                  <th className="text-left py-4 pr-4 font-display text-base text-navy">Feature</th>
                  <th className="text-center py-4 px-4 font-display text-base text-navy">
                    Community<br />
                    <span className="text-xs text-slate-500 font-normal">Free</span>
                  </th>
                  <th className="text-center py-4 px-4 font-display text-base text-saffron">
                    Annual<br />
                    <span className="text-xs text-slate-500 font-normal">£24/yr</span>
                  </th>
                  <th className="text-center py-4 px-4 font-display text-base text-gold">
                    Lifetime<br />
                    <span className="text-xs text-slate-500 font-normal">£250 once</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map(([label, c, a, l]) => (
                  <tr key={label} className="border-b border-navy/5">
                    <td className="py-3.5 pr-4 text-slate-700">{label}</td>
                    <td className="py-3.5 px-4 text-center"><CompareCell value={c} /></td>
                    <td className="py-3.5 px-4 text-center"><CompareCell value={a} /></td>
                    <td className="py-3.5 px-4 text-center"><CompareCell value={l} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-12 text-center">
            <Link
              to={user ? "/profile" : "/register"}
              data-testid="membership-final-cta"
              className="inline-flex items-center gap-2 px-7 py-3 bg-navy text-pearl rounded-full font-medium hover:bg-navy-light"
            >
              {user ? "Go to your profile" : "Start with free Community Membership"} <ArrowRight size={16} />
            </Link>
            <p className="mt-4 text-xs text-slate-500">Payments are handled securely via Stripe.</p>
          </div>
        </div>
      </section>
    </>
  );
}
